import { useState, useEffect, useCallback } from 'react';
import { getMatches, getMatchesAllPages } from '@/api/TournamentManagement/matchApi';
import type { MatchListView, MatchListQuery } from '@/models/tournament';

export function useMatches(query: MatchListQuery) {
  const [data, setData] = useState<MatchListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMatches(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [query.tournamentId, query.search, query.status, query.location, query.pitch, query.ageCategory, query.division, query.completedOnly, query.refereeUserId, query.sortBy, query.sortDescending, query.page, query.pageSize]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * Every match matching the query, not just the first server page. Use for pickers and
 * "all matches" surfaces; `useMatches` is server-paged and clamped to 100 (see `getMatchesAllPages`).
 */
export function useAllMatches(query: Omit<MatchListQuery, 'page' | 'pageSize'> = {}) {
  const [data, setData] = useState<MatchListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMatchesAllPages(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.tournamentId, query.search, query.status]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
